<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Dropdown as VDropdown } from 'floating-vue';
import Icon from 'next/icon/Icon.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import {
  conflictEventUrl,
  formatConflictLabel,
  getAllocationStatusLabel,
  resolveConflictTarget,
} from '../allocationInvalidReason';
import { isSlotInvalid as slotIsInvalid } from '../eventSlotOverlap';
import {
  getCategoryIcon,
  parseAllocationColor,
} from 'dashboard/components-next/planning/Scheduler/allocationCategories';
import { setupTypeLabel } from '../timeslotSetup';

const props = defineProps({
  childAllocations: { type: Array, default: () => [] },
  eventId: { type: String, default: null },
  isAllocationDisco: { type: Function, required: true },
  getResourceName: { type: Function, required: true },
  formatAllocationTime: { type: Function, required: true },
  accentColor: { type: String, default: null },
  collapsed: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  showRemoveBlock: { type: Boolean, default: false },
  addingSlot: { type: Boolean, default: false },
  deletingAllocationId: { type: String, default: null },
});
const emit = defineEmits([
  'toggleCollapse',
  'addTimeslot',
  'deleteAllocation',
  'editAllocation',
  'duplicateAllocation',
  'removeBlock',
]);

const EMPTY_VALUE = '—';

const { t, locale } = useI18n();
const store = useStore();
const openMenuId = ref(null);
const fetchedConflicts = ref({});
let conflictResolveGen = 0;

const removeMenuActions = computed(() =>
  props.showRemoveBlock
    ? [
        {
          key: 'remove-block',
          label: t('PLANNING.SHOW.REMOVE_BLOCK'),
          icon: 'i-lucide-trash-2',
          danger: true,
        },
      ]
    : []
);

const addTimeslotButtonClass =
  'w-full inline-flex items-center justify-center gap-2 h-11 px-4 text-sm font-medium rounded-lg bg-woot-50 text-woot-600 hover:bg-woot-75 dark:bg-woot-900/30 dark:text-woot-300 dark:hover:bg-woot-900/50 transition-colors disabled:opacity-50';

const slotGridClass = 'flex flex-row';

const slotHeaderClass = 'text-xs font-semibold text-n-slate-12 truncate mb-0';

function dayKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

const groupedAllocations = computed(() => {
  const groups = [];

  props.childAllocations.forEach(allocation => {
    const startDate = new Date(allocation.start_at);
    const endDate = new Date(allocation.end_at);
    const start = dayKey(startDate);
    const end = dayKey(endDate);
    const rangeKey = start === end ? start : `${start}_${end}`;

    const last = groups[groups.length - 1];
    if (last?.rangeKey === rangeKey) {
      last.allocations.push(allocation);
      return;
    }

    groups.push({
      key: `${rangeKey}-${allocation.id}`,
      rangeKey,
      startDate,
      endDate,
      allocations: [allocation],
    });
  });

  return groups;
});

function formatDayHeading(date) {
  return date.toLocaleDateString(locale.value, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatGroupHeading(group) {
  const startLabel = formatDayHeading(group.startDate);
  if (dayKey(group.startDate) === dayKey(group.endDate)) {
    return startLabel;
  }
  return t('PLANNING.SHOW.DAY_HEADING_RANGE', {
    start: startLabel,
    end: formatDayHeading(group.endDate),
  });
}

function slotTitle(allocation) {
  return allocation.title || t('PLANNING.SCHEDULER.UNTITLED_SLOT');
}

function slotIcon(allocation) {
  return getCategoryIcon(allocation.category);
}

function slotBarColor(allocation) {
  return parseAllocationColor(allocation) || props.accentColor || '#94a3b8';
}

function slotResourceName(allocation) {
  if (!allocation.resource_id) return t('PLANNING.SHOW.UNASSIGNED_SPACE');
  return props.getResourceName(allocation);
}

function slotSetup(allocation) {
  const setup = allocation.metadata?.setup;
  return setup ? setupTypeLabel(setup, t) : EMPTY_VALUE;
}

function slotPax(allocation) {
  const value = allocation.metadata?.pax;
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  return value;
}

function slotComment(allocation) {
  return allocation.description?.trim() || '';
}

function isSlotInvalid(allocation) {
  return slotIsInvalid(allocation, props.childAllocations);
}

function conflictLabelFor(target) {
  return formatConflictLabel(target, t, {
    getResourceName: props.getResourceName,
    currentEventId: props.eventId,
  });
}

function slotConflict(allocation) {
  if (!isSlotInvalid(allocation)) return null;

  const target =
    allocation.conflicting_allocation || fetchedConflicts.value[allocation.id];
  if (target) {
    return {
      label: conflictLabelFor(target),
      eventUrl: conflictEventUrl(target, props.eventId),
    };
  }

  return {
    label:
      getAllocationStatusLabel(allocation, t) ||
      t('PLANNING.ALLOCATIONS.INVALID_REASONS.TIME_OVERLAP'),
    eventUrl: null,
  };
}

const slotConflicts = computed(() => {
  const map = {};
  props.childAllocations.forEach(allocation => {
    map[allocation.id] = slotConflict(allocation);
  });
  return map;
});

function seedConflictCache(siblings) {
  const next = { ...fetchedConflicts.value };
  const validIds = new Set();

  siblings.forEach(allocation => {
    const isInvalid = slotIsInvalid(allocation, siblings);
    const keepWhileStatusUnknown =
      allocation.status == null && Boolean(next[allocation.id]);
    if (!isInvalid && !keepWhileStatusUnknown) return;
    validIds.add(String(allocation.id));
    if (allocation.conflicting_allocation) {
      next[allocation.id] = allocation.conflicting_allocation;
    }
  });

  Object.keys(next).forEach(id => {
    if (!validIds.has(String(id))) delete next[id];
  });

  fetchedConflicts.value = next;
  return next;
}

watch(
  () =>
    props.childAllocations
      .map(
        allocation =>
          `${allocation.id}:${allocation.status}:${allocation.invalid_reason}:${allocation.resource_id}:${allocation.start_at}:${allocation.end_at}`
      )
      .join('|'),
  async () => {
    conflictResolveGen += 1;
    const gen = conflictResolveGen;
    const siblings = props.childAllocations;
    const next = seedConflictCache(siblings);

    await Promise.all(
      siblings.map(async allocation => {
        if (!slotIsInvalid(allocation, siblings)) return;
        if (allocation.conflicting_allocation) return;
        const target = await resolveConflictTarget({
          allocation,
          siblings,
          store,
        });
        if (target) next[allocation.id] = target;
      })
    );

    if (gen !== conflictResolveGen) return;
    fetchedConflicts.value = { ...next };
  },
  { immediate: true }
);

function isSlotActionsDisabled(allocationId) {
  return props.addingSlot || props.deletingAllocationId === allocationId;
}

function setMenuOpen(allocationId, shown) {
  openMenuId.value = shown ? allocationId : null;
}

function handleMenuAction(allocation, key) {
  openMenuId.value = null;
  if (key === 'edit') emit('editAllocation', allocation);
  if (key === 'duplicate') emit('duplicateAllocation', allocation);
  if (key === 'delete') emit('deleteAllocation', allocation);
}

function addTimeslot() {
  emit('addTimeslot');
}
</script>

<template>
  <SectionCard
    :title="t('PLANNING.SHOW.SCHEDULE')"
    icon="i-lucide-calendar"
    collapsible
    collapse-in-menu
    :collapse-label="t('PLANNING.SHOW.COLLAPSE')"
    :expand-label="t('PLANNING.SHOW.EXPAND')"
    :menu-actions="removeMenuActions"
    flush
    :collapsed="collapsed"
    @toggle-collapse="emit('toggleCollapse')"
    @menu-action="action => action === 'remove-block' && emit('removeBlock')"
  >
    <div v-if="childAllocations.length === 0" class="py-8 text-center pr-12">
      <Icon
        icon="i-lucide-calendar-x"
        class="size-12 mx-auto text-woot-75 mb-2"
      />
      <p class="text-sm text-slate-500 font-semibold">
        {{ t('PLANNING.SHOW.SCHEDULE_EMPTY') }}
      </p>
      <button
        v-if="!readOnly"
        type="button"
        class="mt-4 mx-auto max-w-xs print:hidden"
        :class="[addTimeslotButtonClass]"
        :disabled="addingSlot"
        :aria-label="t('PLANNING.SHOW.ADD_TIMESLOT')"
        @click="emit('addTimeslot')"
      >
        <Icon icon="i-lucide-plus" class="size-4" />
        {{ t('PLANNING.SHOW.ADD_TIMESLOT') }}
      </button>
    </div>

    <div v-else class="pt-1 -ml-8">
      <div
        class="sticky top-0 bg-n-surface-1 z-10 my-4 py-4 border-b border-n-slate-5 gap-4"
        :class="slotGridClass"
      >
        <span class="flex w-[24px] shrink-0 items-center justify-center">
          <span
            class="flex w-2 h-full rounded-full bg-n-slate-4 align-center"
            aria-hidden="true"
          />
        </span>
        <p class="col-span-2 w-[240px] shrink-0" :class="[slotHeaderClass]">
          {{ t('PLANNING.SHOW.TIMESLOT') }}
        </p>
        <p class="col-span-2 w-full" :class="[slotHeaderClass]">
          {{ t('PLANNING.SHOW.TIMESLOT_MODAL.START_TIME') }}
        </p>
        <p class="col-span-2 w-full" :class="[slotHeaderClass]">
          {{ t('PLANNING.SHOW.TIMESLOT_MODAL.END_TIME') }}
        </p>
        <p class="col-span-2 w-full" :class="[slotHeaderClass]">
          {{ t('PLANNING.SHOW.TIMESLOT_MODAL.SPACE') }}
        </p>
        <p class="col-span-2 w-full" :class="[slotHeaderClass]">
          {{ t('PLANNING.SHOW.TIMESLOT_MODAL.SETUP') }}
        </p>
        <p class="col-span-2 w-full" :class="[slotHeaderClass]">
          {{ t('PLANNING.SHOW.TIMESLOT_MODAL.GUESTS') }}
        </p>
        <p class="w-10 shrink-0" />
      </div>

      <div
        v-for="group in groupedAllocations"
        :key="group.key"
        class="mb-2 last:mb-0"
      >
        <div class="py-4 gap-4" :class="slotGridClass">
          <span class="flex w-[24px] shrink-0 items-center justify-center">
            <span
              class="flex w-2 h-full rounded-full bg-n-slate-4 shrink-0"
              aria-hidden="true"
            />
          </span>
          <h3 class="col-span-8 text-sm font-bold mb-0">
            {{ formatGroupHeading(group) }}
          </h3>
        </div>
        <div class="schedule-day-contaner divide-y divide-n-slate-5">
          <div
            v-for="allocation in group.allocations"
            :key="allocation.id"
            class="schedule-slot-container flex flex-col py-3 print:break-inside-avoid"
            :class="{
              'bg-red-50/50 dark:bg-red-900/10 -mx-2 px-2 rounded-lg':
                isSlotInvalid(allocation),
            }"
          >
            <div
              class="flex flex-row gap-4 w-full items-center print:gap-2 print:items-start"
            >
              <span class="flex w-[24px] shrink-0 items-center justify-center">
                <span
                  class="flex w-2 h-full rounded-full shrink-0 min-h-12"
                  :class="{ 'is-disco': isAllocationDisco(allocation) }"
                  :style="{ backgroundColor: slotBarColor(allocation) }"
                />
              </span>
              <div class="flex items-center gap-2 w-[240px] shrink-0 gap-3">
                <Icon
                  v-if="slotIcon(allocation)"
                  :icon="`i-lucide-${slotIcon(allocation)}`"
                  class="size-4 text-n-slate-9 dark:text-n-slate-2 shrink-0"
                />
                <p
                  class="font-semibold text-n-slate-12 line-clamp-2 leading-tight mb-0 w-full"
                >
                  {{ slotTitle(allocation) }}
                </p>
              </div>
              <p class="text-sm text-n-slate-11 tabular-nums mb-0 w-full">
                {{ formatAllocationTime(allocation.start_at) }}
              </p>
              <p class="text-sm text-n-slate-11 tabular-nums mb-0 w-full">
                {{ formatAllocationTime(allocation.end_at) }}
              </p>
              <p class="text-sm text-n-slate-11 truncate mb-0 w-full">
                {{ slotResourceName(allocation) }}
              </p>
              <p class="text-sm text-n-slate-11 truncate mb-0 w-full">
                {{ slotSetup(allocation) }}
              </p>
              <p class="text-sm text-n-slate-11 tabular-nums mb-0 w-full">
                {{ slotPax(allocation) }}
              </p>
              <div
                v-if="!readOnly"
                class="flex justify-end print:hidden items-center w-10"
                @click.stop
                @mousedown.stop
              >
                <VDropdown
                  :shown="openMenuId === allocation.id"
                  :triggers="['click']"
                  placement="bottom-end"
                  :distance="8"
                  :arrow="false"
                  popper-class="event-menu-popper"
                  @update:shown="shown => setMenuOpen(allocation.id, shown)"
                >
                  <button
                    type="button"
                    class="flex items-center justify-center size-10 rounded-md text-n-slate-10 hover:bg-n-slate-4 hover:text-n-slate-12 disabled:opacity-50"
                    :disabled="isSlotActionsDisabled(allocation.id)"
                    :aria-label="t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
                  >
                    <Icon icon="i-lucide-ellipsis" class="size-4" />
                  </button>
                  <template #popper>
                    <div
                      class="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-75 dark:border-slate-800 p-1.5 flex flex-col min-w-44"
                      @click.stop
                      @mousedown.stop
                    >
                      <button
                        type="button"
                        class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12 disabled:opacity-50"
                        :disabled="isSlotActionsDisabled(allocation.id)"
                        @click="handleMenuAction(allocation, 'edit')"
                      >
                        <Icon
                          icon="i-lucide-pencil"
                          class="size-4 text-n-slate-11"
                        />
                        <span>{{ t('PLANNING.SHOW.EDIT_SLOT') }}</span>
                      </button>
                      <button
                        type="button"
                        class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12 disabled:opacity-50"
                        :disabled="isSlotActionsDisabled(allocation.id)"
                        @click="handleMenuAction(allocation, 'duplicate')"
                      >
                        <Icon
                          icon="i-lucide-copy"
                          class="size-4 text-n-slate-11"
                        />
                        <span>{{ t('PLANNING.SHOW.DUPLICATE_SLOT') }}</span>
                      </button>
                      <button
                        type="button"
                        class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2.5 text-red-600 disabled:opacity-50"
                        :disabled="isSlotActionsDisabled(allocation.id)"
                        @click="handleMenuAction(allocation, 'delete')"
                      >
                        <Icon icon="i-lucide-trash-2" class="size-4" />
                        <span>{{ t('PLANNING.SHOW.SLOT_MENU_DELETE') }}</span>
                      </button>
                    </div>
                  </template>
                </VDropdown>
              </div>
              <div v-else class="print:hidden" />
            </div>
            <!-- END SLOT INNER -->
            <div class="w-full pl-10">
              <p
                v-if="slotComment(allocation)"
                class="mb-0 text-sm text-n-slate-10"
              >
                {{ slotComment(allocation) }}
              </p>
              <p
                v-if="slotConflicts[allocation.id]"
                class="mt-1 mb-0 text-xs text-red-500 dark:text-red-400"
              >
                <router-link
                  v-if="slotConflicts[allocation.id].eventUrl"
                  :to="slotConflicts[allocation.id].eventUrl"
                  class="underline hover:text-red-600 dark:hover:text-red-300"
                >
                  {{ slotConflicts[allocation.id].label }}
                </router-link>
                <span v-else>{{ slotConflicts[allocation.id].label }}</span>
              </p>
            </div>
          </div>
          <!-- END SLOT CONTAINER-->
        </div>
        <!-- END DAY CONTAINER-->
      </div>

      <div v-if="!readOnly" class="pt-3 print:hidden">
        <button
          type="button"
          :class="addTimeslotButtonClass"
          :disabled="addingSlot"
          :aria-label="t('PLANNING.SHOW.ADD_TIMESLOT')"
          @click="addTimeslot"
        >
          <Icon icon="i-lucide-plus" class="size-4" />
          {{ t('PLANNING.SHOW.ADD_TIMESLOT') }}
        </button>
      </div>
    </div>
  </SectionCard>
</template>

<style scoped>
.is-disco {
  background-color: transparent;
  background-image: linear-gradient(
    90deg,
    #ff4d4d,
    #ff9a3c,
    #ffd93d,
    #6bcf63,
    #4dd0e1,
    #5c9bff,
    #a56bff,
    #ff5ce0
  );
  animation: discoHue 4s linear infinite;
}

@keyframes discoHue {
  0% {
    filter: hue-rotate(0deg);
  }
  100% {
    filter: hue-rotate(360deg);
  }
}
</style>
